import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { supabase } from "@/integrations/supabase/client";
import Loader from "@/components/ui/loader";

interface ResetPasswordFormProps {
  onBackToLogin: () => void;
}

const ResetPasswordForm = ({ onBackToLogin }: ResetPasswordFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Richiesta",
        description: "Inserisci il tuo indirizzo email.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        let errorMessage = "Errore durante l'invio dell'email di reset.";
        
        if (error.message.includes("User not found")) {
          errorMessage = "Nessun account trovato con questo indirizzo email.";
        } else if (error.message.includes("Too many requests")) {
          errorMessage = "Troppi tentativi. Aspetta un momento e riprova.";
        }
        
        toast({
          title: "Errore",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        setIsEmailSent(true);
        toast({
          title: "Email Inviata! 📧",
          description: "Controlla la tua email per il link di reset della password.",
        });
      }
    } catch (err) {
      toast({
        title: "Errore",
        description: "Si è verificato un errore imprevisto. Riprova.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Email Inviata!
          </h3>
          <p className="text-gray-600 mb-6">
            Abbiamo inviato un link di reset della password a <strong>{email}</strong>.
            Controlla la tua casella email e segui le istruzioni.
          </p>
          <p className="text-sm text-gray-500">
            Non hai ricevuto l'email? Controlla la cartella spam o riprova.
          </p>
        </div>
        
        <div className="space-y-3">
          <Button 
            onClick={() => {
              setIsEmailSent(false);
              setEmail('');
            }}
            variant="outline"
            className="w-full border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Invia di nuovo
          </Button>
          
          <Button 
            onClick={onBackToLogin}
            variant="ghost"
            className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Reset Password
        </h3>
        <p className="text-gray-600">
          Inserisci il tuo indirizzo email e ti invieremo un link per reimpostare la password.
        </p>
      </div>

      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reset-email" className="text-sm font-medium text-gray-700">
            Indirizzo Email
          </Label>
          <Input 
            id="reset-email" 
            type="email" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 border-gray-200 focus:border-[#030A14] focus:ring-[#030A14]"
            placeholder="nome@esempio.com"
            required 
            disabled={isLoading}
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-[#030A14] hover:bg-[#030A14]/90 text-white font-medium transition-colors duration-200"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader size="sm" className="mr-3" />
              Invio in corso...
            </>
          ) : (
            <>
              <Mail className="w-5 h-5 mr-2" />
              Invia Link di Reset
            </>
          )}
        </Button>
      </form>

      <div className="text-center">
        <Button 
          onClick={onBackToLogin}
          variant="ghost"
          className="w-full text-gray-600 hover:text-gray-800 hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna al login
        </Button>
      </div>
    </div>
  );
};

export default ResetPasswordForm; 